import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useRoutes } from 'react-router-dom'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { SessionType } from '@web-apps/feature-auth'
import { useGetCreatorPageQuery } from '@web-apps/feature-creator-page'
import { LoadingIndicator, SuspenseWrapper } from '@web-apps/ui-shared'
import { isSafariIOS } from '@web-apps/utils-shared'

import {
  AddToHomeScreenTooltip,
  AdminTooltip,
  ErrorApp,
  Sidebar,
} from '../../components'

import {
  getRoutesComponents,
  injectNonPublicRouteComponentsForRoles,
} from '../../routes/RoutesComponents'
import { useGetUserAccountQuery } from '../../services/user.api'
import { AccountContext, CreatorPageContext } from '../../utils/contexts'
import { useAccountLocale } from '../../utils/hooks/useAccountLocale.hooks'
import { useIsDeprecatedFeaturesDisabled } from '../../utils/hooks/useDeprecatedFeatures.hooks'

import { useUnleashContext } from '@unleash/proxy-client-react'
import { useGetAPIConfigsQuery } from '../../services/api'
import { DATE_OF_HIDDEN_DESIGN_OPTIONS } from '../../utils/constants/app.constants'
import { RN_WEBVIEW_SESSION_STORAGE_KEY } from '../../utils/helpers/rnWebView.helpers'
import { StyledAppWrapper, StyledContainer } from './PageLayout.styles'

type Props = {
  userId: string
  session: SessionType
}

export const PageLayout = ({ userId, session }: Props) => {
  const { t } = useTranslation(['app'])
  const updateContext = useUnleashContext()
  const {
    data: APIConfigs,
    isLoading: isLoadingAPIConfigs,
    isError: isAPIConfigsError,
  } = useGetAPIConfigsQuery()

  const {
    data,
    isLoading: isAccountLoading,
    isError: isAccountError,
  } = useGetUserAccountQuery({
    userId,
  })
  const {
    data: creatorPage,
    isLoading: isCreatorPageLoading,
    isFetching: isFetchingCreatorPage,
    isError: isCreatorPageError,
  } = useGetCreatorPageQuery(
    { pageId: data?.pageId || '' },
    { skip: !data?.pageId }
  )

  const { isLanguageLoading } = useAccountLocale(data?.locale)

  const isDeprecatedFeaturesDisabled = useIsDeprecatedFeaturesDisabled()

  const userRoles = session.identity.metadata_public?.roles || []
  const RouteComponentsForRoles = injectNonPublicRouteComponentsForRoles(
    userRoles,
    getRoutesComponents(isDeprecatedFeaturesDisabled)
  )
  const routes = useRoutes(RouteComponentsForRoles)

  const isRNWebView = sessionStorage.getItem(RN_WEBVIEW_SESSION_STORAGE_KEY)
  const isSidebarVisible = !isRNWebView && !isDeprecatedFeaturesDisabled

  useEffect(() => {
    if (data) {
      if (
        new Date(data.createdAt).getTime() >
        new Date(DATE_OF_HIDDEN_DESIGN_OPTIONS).getTime()
      )
        updateContext({
          userId: data.userId,
          properties: {
            userSlug: data.slug,
            hideDesignOptions: 'true',
          },
        })
    }
  }, [data, updateContext])

  useEffect(() => {
    if (isCreatorPageError || isAccountError || isAPIConfigsError)
      toast.error(t('app:page_error'))
  }, [isCreatorPageError, isAccountError, isAPIConfigsError, t])

  if (
    isAccountLoading ||
    isCreatorPageLoading ||
    isLoadingAPIConfigs ||
    isLanguageLoading
  ) {
    return <LoadingIndicator size={100} isCentered />
  }

  if (!data || !creatorPage || !APIConfigs) return <ErrorApp />

  return (
    <ErrorBoundary fallback={<ErrorApp />}>
      <AccountContext.Provider
        value={{
          ...data,
          apiConfigs: APIConfigs,
        }}
      >
        <CreatorPageContext.Provider
          value={{
            ...creatorPage,
            queryStatus: {
              isLoading: isCreatorPageLoading,
              isFetching: isFetchingCreatorPage,
              isError: isCreatorPageError,
            },
          }}
        >
          <StyledAppWrapper $hasSidebar={isSidebarVisible}>
            <AdminTooltip />
            {!isRNWebView && <AddToHomeScreenTooltip />}
            {isSidebarVisible && <Sidebar />}
            <StyledContainer $isSafariIOS={isSafariIOS()}>
              <SuspenseWrapper>{routes}</SuspenseWrapper>
            </StyledContainer>
          </StyledAppWrapper>
        </CreatorPageContext.Provider>
      </AccountContext.Provider>
    </ErrorBoundary>
  )
}
